/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	Box,
	Button,
	Center,
	Container,
	Flex,
	Image,
	Radio,
	RadioGroup,
	Stack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	useDisclosure,
	useToast,
	Switch,
	SimpleGrid
} from '@chakra-ui/react';
import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import CamdigiKeyLogo from '../../assets/images/camdigikey-no-bg.png';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import ImageCropFormField from '../../components/form/ImageCropFormFieldSignature';
import ImagePickerAvatar from '../../components/ImagePicker/ImagePickerAvatar';
import CanvasFormField from '../../components/form/CanvasFormField';
import useStore from '../../store';
import c from '../../constant';
import * as Yup from 'yup';

const MAX_FILE_SIZE = 104857600 / 20;

const QUERY_CURRENT_USER = gql`
	query {
		getCurrentuser {
			id
			username
			role
			fullName
			fullNameKhmer
			canAssignUserToCompanyProposal
			avatar
			camDigiKeyId
			isTwoFactorEnabled
		}
	}
`;

const CHANGE_AVATAR_MUTATION = gql`
	mutation change($avatar: JSON!) {
		changeAvatar(avatar: $avatar)
	}
`;

const MUTATION_UNLINK_CAMDIGIKEY = gql`
	mutation {
		unlinkCamDigiKey
	}
`;

const QUERY_SIGNATURE = gql`
	query get($id: String!) {
		getSignatureByUserId(id: $id) {
			id
			signatureImage
		}
	}
`;

const CREATE_SIGNATURE = gql`
	mutation createSignature($attachmentFileSize: Float, $attachmentFile: Upload, $attachmentBase64: String) {
		createSignature(attachmentFileSize: $attachmentFileSize, attachmentFile: $attachmentFile, attachmentBase64: $attachmentBase64) {
			id
			signatureImage
		}
	}
`;

const DELETE_SIGNATURE = gql`
	mutation deleteSignature($id: String!) {
		deleteSignature(id: $id)
	}
`;

function Account() {
	const toast = useToast();
	const { t } = useTranslation();
	const [loadCurrentUser, { data }] = useLazyQuery(QUERY_CURRENT_USER);
	const setCurrentUser = useStore((state) => state.setCurrentUser);

	const [loadSignature, { loading: loadingSignature, data: dataSignature }] = useLazyQuery(QUERY_SIGNATURE);

	const [changeAvatarMutation, { loading: loadingChangeAvatar, error: errorChangeAvatar, data: dataChangeAvatar }] = useMutation(CHANGE_AVATAR_MUTATION);

	const [unlinkCamDigiKeyMutation, { loading: loadingUnlink, data: dataUnlink }] = useMutation(MUTATION_UNLINK_CAMDIGIKEY);

	const [removeSignatureMutation, { loading: loadingRemoveSignature, data: dataRemoveSignature }] = useMutation(DELETE_SIGNATURE);

	const [createSignatureMutation, { data: dataCreateSignature }] = useMutation(CREATE_SIGNATURE);

	useEffect(() => {
		if (dataCreateSignature) {
			loadSignature({
				variables: {
					id: data?.getCurrentuser?.id,
				},
			});
		}
	}, [dataCreateSignature]);

	const { isOpen, onOpen, onClose } = useDisclosure();
	const cancelRef = useRef();

	useEffect(() => {
		if (dataChangeAvatar?.changeAvatar) {
			toast({
				title: t('Avatar changed successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
			loadCurrentUser();
		}
	}, [dataChangeAvatar]);

	useEffect(() => {
		if (errorChangeAvatar) {
			errorChangeAvatar.graphQLErrors.map(({ message }, i) =>
				toast({
					title: message,
					status: 'error',
					isClosable: true,
					position: 'top-right',
				})
			);
		}
	}, [errorChangeAvatar]);

	useEffect(() => {
		window.scrollTo(0, 0);
		loadCurrentUser();
	}, []);

	useEffect(() => {
		if (data) {
			setCurrentUser(data?.getCurrentuser);
			loadSignature({
				variables: {
					id: data?.getCurrentuser?.id,
				},
			});
		}
	}, [data]);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				isSignatureImage: Yup.boolean(),
				signatureImage: Yup.mixed().when('isSignatureImage', {
					is: (isSignatureImage) => isSignatureImage === true,
					then: Yup.mixed()
						.test('fileSize', 'over limit', (value) => {
							if (value) {
								return value?.size <= MAX_FILE_SIZE;
							} else {
								return true;
							}
						})
						.required('required'),
					otherwise: Yup.mixed().nullable(),
				}),
				signatureCanvas: Yup.mixed().when('isSignatureImage', {
					is: (isSignatureImage) => isSignatureImage === false,
					then: Yup.string().required('required'),
					otherwise: Yup.string().nullable(),
				}),
			}),
		[]
	);

	function row(label, value) {
		return (
			<Flex p="2" _hover={{ bg: 'gray.200' }}>
				<Text mr="2" fontWeight="500" w="150px">
					{t(label)}
				</Text>
				<Box>
					{value?.file ? (
						<Box>
							<a href={value?.file} download>
								<Text color="blue.300">Download ({`${parseFloat(value?.fileSize / 1048576).toFixed(2)}`}MB)</Text>
							</a>
						</Box>
					) : (
						<Box>
							<Text>:&nbsp;{value || ''}</Text>
						</Box>
					)}
				</Box>
			</Flex>
		);
	}

	let defaultFont = 'Hanuman';
	let setFont = localStorage.getItem('font');
	if (setFont) {
		defaultFont = setFont;
	}

	const [loadingCamdigikey, setLoadingCamdigikey] = useState(false);
	function linkWithCamDigiKey() {
		const accessToken = sessionStorage.getItem('accessToken');
		setLoadingCamdigikey(true);
		localStorage.setItem('adminAccessToken', accessToken);
		sessionStorage.clear();
		window.location.href = c.adminCamDigiKeyRedirectUrlLink;
	}

	function unlinkWithCamDigiKey() {
		unlinkCamDigiKeyMutation();
	}

	useEffect(() => {
		if (dataUnlink) {
			toast({
				title: t('CamDigiKey unlinked successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
			onClose();
			window.scrollTo(0, 0);
			loadCurrentUser();
		}
	}, [dataUnlink]);

	function removeSignature(id) {
		removeSignatureMutation({
			variables: {
				id,
			},
		});
	}

	useEffect(() => {
		if (dataCreateSignature?.createSignature) {
			onClose();
			loadSignature({
				variables: {
					id: data?.getCurrentuser?.id,
				},
			});
			toast({
				title: t('Signature uploaded successfully'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
		}
	}, [dataCreateSignature]);

	useEffect(() => {
		if (dataRemoveSignature?.deleteSignature) {
			onClose();
			loadSignature({
				variables: {
					id: data?.getCurrentuser?.id,
				},
			});
			toast({
				title: t('Signature has been removed'),
				status: 'success',
				isClosable: true,
				position: 'top-right',
			});
		}
	}, [dataRemoveSignature]);

	return (
		<>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: t('Account'),
							path: '#',
						},
					]}
				/>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Tabs isLazy mt="2">
							<TabList>
								<Tab>{t('Account')}</Tab>
								<Tab>{t('Signature')}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<SimpleGrid columns={[1, 2]} spacing={10}>
											<Box w="200px" overflow="hidden">
												<ImagePickerAvatar
													label={t('Avatar')}
													onContentChange={(e) => {
														changeAvatarMutation({
															variables: {
																avatar: e?.icon && e?.medium ? e : null,
															},
														});
													}}
													initContent={data?.getCurrentuser?.avatar}
												/>
												{loadingChangeAvatar && <Text>Saving...</Text>}
											</Box>
											{/* {row('Username', data?.getCurrentuser?.username)}
											{row('Full Name In Latin', data?.getCurrentuser?.fullName)}
											{row('Full Name In Khmer', data?.getCurrentuser?.fullNameKhmer)} */}
											
											<Box>

											<Link to="/change-password">
												<Button colorScheme='blue' variant='outline' mt="0" w="100%" borderRadius={c.borderRadius}>
													<Text  color="blue.300">🔑✏️ {t('Change Password')}</Text>
												</Button>
											</Link>

											<Link to="/totp">
												<Button colorScheme='blue' variant='outline' mt="4" w="100%" borderRadius={c.borderRadius}>
													<Text  color="blue.300">🔐📱 {t('Two-Factor Authentication')}</Text>
												</Button>
											</Link>

											{data?.getCurrentuser?.camDigiKeyId && (
												<Button
													mt="4"
													variant="outline"
													size="md"
													h="42px"
													w="100%"
													colorScheme="red"
													isLoading={false}
													loadingText={t('Loading')}
													spinnerPlacement="start"
													onClick={onOpen}
													leftIcon={<Image src={CamdigiKeyLogo} w="20px" h="20px" />}
												>
													{t(`Unlink with CamDigiKey`)}
												</Button>
											)}

											{!data?.getCurrentuser?.camDigiKeyId && (
												<Button
													mt="4"
													variant="outline"
													size="md"
													h="42px"
													w="100%"
													colorScheme="blue"
													isLoading={loadingCamdigikey}
													loadingText={t('Loading')}
													spinnerPlacement="start"
													onClick={() => linkWithCamDigiKey()}
													leftIcon={<Image src={CamdigiKeyLogo} w="20px" h="20px" />}
												>
													{t(`Link with CamDigiKey`)}
												</Button>
											)}


											{/* <RadioGroup
												defaultValue={defaultFont}
												mt="4"
												onChange={(e) => {
													localStorage.setItem('font', e);
													window.location.reload();
												}}
											>
												<Stack spacing={5} direction="row">
													<Text>{t('Font')}:</Text>

													<Radio value="Hanuman">Hanuman</Radio>
													<Radio value="MEF1">MEF1</Radio>
													<Radio value="Nokora">Nokora</Radio>
												</Stack>
											</RadioGroup> */}
											
											</Box>
											
									</SimpleGrid>
								</TabPanel>
								<TabPanel>
									<Box p="16px" maxW="400px">
										{data?.getCurrentuser?.id && !loadingSignature && (
											<>
												{dataSignature?.getSignatureByUserId?.id ? (
													<>
														<Image
															w="240px"
															h="135px"
															src={dataSignature?.getSignatureByUserId?.signatureImage?.file}
															alt="Signature"
														/>
														<Button
															isLoading={loadingRemoveSignature}
															borderRadius={c.borderRadius}
															colorScheme="red"
															mt={8}
															onClick={() => removeSignature(dataSignature?.getSignatureByUserId?.id)}
														>
															{t('Remove Signature')}
														</Button>
													</>
												) : (
													<Formik
														initialValues={{ isSignatureImage: true, signatureImage: null, signatureCanvas: '' }}
														validationSchema={validationSchema}
														onSubmit={(values) => {
															createSignatureMutation({
																variables: {
																	attachmentFileSize: values?.signatureImage?.size || null,
																	attachmentFile: values?.signatureImage || null,
																	attachmentBase64: values?.signatureCanvas || null,
																},
															});
														}}
													>
														{(props) => {
															return (
																<Form>
																	<Flex mb={4}>
																		<Text fontSize="md">{t('Choose file')}</Text>
																		<Switch
																			ml={4}
																			mr={4}
																			onChange={() =>
																				props?.setFieldValue('isSignatureImage', !props?.values['isSignatureImage'])
																			}
																		/>
																		<Text fontSize="md">{t('Write')}</Text>
																	</Flex>
																	{props?.values['isSignatureImage'] && (
																		<>
																			<ImageCropFormField
																				{...props}
																				label="Signature"
																				name="signatureImage"
																				maxFileSize={MAX_FILE_SIZE}
																				required
																				aspect={16 / 9}
																				resizedToWidth={240}
																			/>
																		</>
																	)}
																	{!props?.values['isSignatureImage'] && (
																		<>
																			<CanvasFormField
																				{...props}
																				label="Signature"
																				name="signatureCanvas"
																				required={true}
																			/>
																		</>
																	)}
																	<Flex w="100%" justifyContent="flex-start">
																		<Button
																			id="login-btn"
																			variant="solid"
																			size="md"
																			h="42px"
																			w="110px"
																			type="submit"
																			borderRadius={c.borderRadius}
																			mt="16px"
																			colorScheme="blue"
																			rightIcon={
																				<FontAwesomeIcon
																					style={{
																						color: 'white',
																						fontSize: 16,
																						cursor: 'pointer',
																					}}
																					icon={faLongArrowAltRight}
																				/>
																			}
																		>
																			{t('Submit')}
																		</Button>
																	</Flex>
																</Form>
															);
														}}
													</Formik>
												)}
											</>
										)}
									</Box>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</Box>
				</Center>
			</Container>
			<AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Unlink CamDigiKey')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure?')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={onClose}>
								{t('Cancel')}
							</Button>
							<Button isLoading={loadingUnlink} colorScheme="red" onClick={unlinkWithCamDigiKey} ml={3}>
								{t('Unlink')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}

export default Account;
